/**
 * OnboardingWizard - Multi-step onboarding flow
 * Step 1: Configure rules → Step 2: Upload → Step 3: Preview → Step 4: Importing → Step 5: Results
 */

import React, { useState, useCallback } from 'react';
import Papa from 'papaparse';
import { Check, AlertTriangle, ArrowRight, FileWarning, Loader2 } from 'lucide-react';
import { Button } from '../shadcn/button';
import { SelectStep, PreviewStep } from './UploadWizard';
import RulesConfigStep from './onboarding/RulesConfigStep';
import { parseCSV, buildExistingPatientsMap } from '../../utils/csvParser';
import { calculateBatchRisk, getRiskStats } from '../../utils/riskRules';
import { translateAllFlags, getPrimaryFlag, generateSuggestedApproach } from '../../utils/flagTranslator';
import { api } from '../../lib/api';
import { getCurrentUserWithClinic } from '../../lib/getCurrentUserWithClinic';

// Wizard steps
const STEPS = {
  RULES: 'rules',
  SELECT: 'select',
  PREVIEW: 'preview',
  IMPORTING: 'importing',
  RESULTS: 'results',
  ERROR: 'error',
};

const OnboardingWizard = ({ onComplete }) => {
  const [step, setStep] = useState(STEPS.RULES);
  const [parseResult, setParseResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [importProgress, setImportProgress] = useState(0);

  // Handle file selection (same logic as UploadWizard)
  const handleFileSelect = useCallback(async (selectedFile) => {
    setError(null);

    try {
      const { clinicId } = await getCurrentUserWithClinic();

      // Fetch existing patients for duplicate detection
      const patientsData = await api.getPatients();
      const existingPatients = patientsData.patients || patientsData || [];

      const existingMap = buildExistingPatientsMap(existingPatients, clinicId);

      // Parse CSV
      Papa.parse(selectedFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            setError({
              type: 'PARSE_ERROR',
              message: 'Couldn\'t read this file. Make sure it\'s a valid CSV.',
            });
            setStep(STEPS.ERROR);
            return;
          }

          const headers = results.meta.fields || [];
          const parsed = parseCSV(results.data, headers, existingMap, clinicId);

          if (!parsed.success) {
            setError({
              type: parsed.error,
              message: parsed.message,
            });
            setStep(STEPS.ERROR);
            return;
          }

          if (parsed.patients.length === 0) {
            setError({
              type: 'NO_PATIENTS',
              message: 'No valid patients found in this file. All rows were skipped.',
            });
            setStep(STEPS.ERROR);
            return;
          }

          setParseResult(parsed);
          setStep(STEPS.PREVIEW);
        },
        error: () => {
          setError({
            type: 'PARSE_ERROR',
            message: 'Couldn\'t read this file. Make sure it\'s a valid CSV.',
          });
          setStep(STEPS.ERROR);
        },
      });
    } catch (err) {
      console.error('File processing error:', err);
      setError({
        type: 'UNKNOWN_ERROR',
        message: 'Something went wrong. Please try again.',
      });
      setStep(STEPS.ERROR);
    }
  }, []);

  // Handle import (same logic as UploadWizard)
  const handleImport = useCallback(async () => {
    if (!parseResult?.patients?.length) return;

    setStep(STEPS.IMPORTING);
    setImportProgress(10);

    try {
      // Get clinic rules for risk calculation
      const rulesData = await api.getClinicRules();
      const clinicRules = rulesData.rules || [];

      // Build rule config map
      const ruleConfig = {};
      for (const rule of clinicRules) {
        if (rule.rule_code) {
          ruleConfig[rule.rule_code] = rule.config;
        }
      }

      setImportProgress(20);

      // Calculate risk for all patients
      const patientsWithRisk = calculateBatchRisk(parseResult.patients, ruleConfig);

      // Translate EHR flags into human-readable sentences
      const patientsWithFlags = patientsWithRisk.map(patient => {
        const translated = translateAllFlags(patient);
        const primary = getPrimaryFlag(translated);
        return {
          ...patient,
          translated_flags: translated.length > 0 ? translated : undefined,
          primary_flag: primary?.display_text || undefined,
          suggested_approach: generateSuggestedApproach(patient, translated) || undefined,
        };
      });

      setImportProgress(40);

      // Bulk create patients via API
      const response = await api.createPatients(patientsWithFlags);
      const inserted = response.patients || [];

      setImportProgress(95);

      // Update clinic's first_upload_at
      await api.updateClinic({ first_upload_at: new Date().toISOString() });

      setImportProgress(100);

      // Calculate final stats
      const riskStats = getRiskStats(inserted);

      setImportResult({
        imported: inserted.length,
        riskStats,
        missingContact: parseResult.stats.missingContact,
        defaultedRisk: riskStats.defaulted,
        skipped: parseResult.skipped,
      });

      setStep(STEPS.RESULTS);
    } catch (err) {
      console.error('Import error:', err);
      setError({
        type: 'IMPORT_ERROR',
        message: 'Failed to import patients. Please try again.',
      });
      setStep(STEPS.ERROR);
    }
  }, [parseResult]);

  // Handle finish
  const handleFinish = useCallback(() => {
    if (onComplete) {
      onComplete(importResult);
    }
  }, [onComplete, importResult]);

  // Get step number for indicator (1-indexed)
  const getStepNumber = () => {
    switch (step) {
      case STEPS.RULES: return 1;
      case STEPS.SELECT: return 2;
      case STEPS.PREVIEW: return 3;
      case STEPS.IMPORTING: return 4;
      case STEPS.RESULTS: return 4;
      case STEPS.ERROR: return 2;
      default: return 1;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`
                w-2 h-2 rounded-full transition-colors
                ${s === getStepNumber() ? 'bg-slate-900' : s < getStepNumber() ? 'bg-slate-400' : 'bg-slate-200'}
              `}
            />
          ))}
        </div>

        {/* Card Container */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
          {/* RULES STEP */}
          {step === STEPS.RULES && (
            <RulesConfigStep
              onNext={() => setStep(STEPS.SELECT)}
              onUseDefaults={() => setStep(STEPS.SELECT)}
            />
          )}

          {/* SELECT STEP */}
          {step === STEPS.SELECT && (
            <div className="space-y-6">
              <div className="text-center">
                <h1 className="text-xl font-bold text-slate-900 mb-2">
                  Now upload your patient list
                </h1>
                <p className="text-sm text-slate-500">
                  We'll apply your rules and prioritize your queue
                </p>
              </div>

              <SelectStep onFileSelect={handleFileSelect} />

              <p className="text-xs text-slate-400 text-center">
                Works with exports from Epic, Cerner, Athena, and more
              </p>

              <button
                type="button"
                onClick={() => setStep(STEPS.RULES)}
                className="w-full text-sm text-slate-500 hover:text-slate-700"
              >
                ← Back to rules
              </button>
            </div>
          )}

          {/* PREVIEW STEP */}
          {step === STEPS.PREVIEW && parseResult && (
            <div className="space-y-4">
              <div className="text-center mb-2">
                <h1 className="text-xl font-bold text-slate-900 mb-1">
                  Preview import
                </h1>
                <p className="text-sm text-slate-500">
                  Review before importing
                </p>
              </div>

              <PreviewStep
                result={parseResult}
                onCancel={() => setStep(STEPS.SELECT)}
                onImport={handleImport}
              />
            </div>
          )}

          {/* IMPORTING STEP */}
          {step === STEPS.IMPORTING && (
            <div className="py-8 text-center">
              <Loader2 className="h-8 w-8 text-slate-400 animate-spin mx-auto mb-4" />
              <p className="text-sm font-medium text-slate-900 mb-2">
                Importing patients...
              </p>
              <p className="text-xs text-slate-500 mb-4">
                Calculating risk based on your rules
              </p>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-slate-900 transition-all duration-300"
                  style={{ width: `${importProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* RESULTS STEP */}
          {step === STEPS.RESULTS && importResult && (
            <ResultsStep
              result={importResult}
              onFinish={handleFinish}
            />
          )}

          {/* ERROR STEP */}
          {step === STEPS.ERROR && error && (
            <ErrorStep
              error={error}
              onRetry={() => {
                setStep(STEPS.SELECT);
                setError(null);
              }}
            />
          )}
        </div>

        {/* Branding */}
        <p className="text-center text-xs text-slate-400 mt-6">
          Recoverly — Patient Outreach, Simplified
        </p>
      </div>
    </div>
  );
};

// RESULTS STEP - Custom for onboarding
const ResultsStep = ({ result, onFinish }) => {
  const { riskStats, missingContact, defaultedRisk } = result;

  return (
    <div className="space-y-5">
      {/* Success icon */}
      <div className="flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
          <Check className="h-8 w-8 text-emerald-600" />
        </div>
      </div>

      {/* Success message */}
      <div className="text-center">
        <h1 className="text-xl font-bold text-slate-900 mb-2">
          {result.imported} patients prioritized
        </h1>
        <p className="text-sm text-slate-500">
          based on the rules you configured
        </p>
      </div>

      {/* Risk breakdown */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-red-50 rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-red-600">{riskStats.high}</p>
          <p className="text-xs text-red-600 font-medium mt-1">High</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-amber-600">{riskStats.medium}</p>
          <p className="text-xs text-amber-600 font-medium mt-1">Medium</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-emerald-600">{riskStats.low}</p>
          <p className="text-xs text-emerald-600 font-medium mt-1">Low</p>
        </div>
      </div>

      {/* Warnings */}
      <div className="space-y-2">
        {defaultedRisk > 0 && (
          <div className="flex items-start gap-2 text-amber-600 text-sm bg-amber-50 rounded-lg p-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              {defaultedRisk} patients couldn't be prioritized — defaulted to Needs Attention
            </span>
          </div>
        )}
        {missingContact > 0 && (
          <div className="flex items-start gap-2 text-slate-600 text-sm bg-slate-50 rounded-lg p-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              {missingContact} patients have no contact info yet
            </span>
          </div>
        )}
      </div>

      {/* Action */}
      <Button
        onClick={onFinish}
        className="w-full bg-slate-900 hover:bg-slate-800 text-white py-5 text-base"
      >
        Go to Triage
        <ArrowRight className="h-5 w-5 ml-2" />
      </Button>
    </div>
  );
};

// ERROR STEP
const ErrorStep = ({ error, onRetry }) => {
  return (
    <div className="py-6 text-center">
      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
        <FileWarning className="h-6 w-6 text-red-600" />
      </div>
      <p className="text-sm font-medium text-slate-900 mb-2">
        {error.type === 'NO_PATIENT_IDENTIFIER' ? 'No patient names found' : 'Upload failed'}
      </p>
      <p className="text-xs text-slate-500 mb-6 max-w-xs mx-auto">
        {error.message}
      </p>
      <Button onClick={onRetry} variant="outline">
        Try Another File
      </Button>
    </div>
  );
};

export default OnboardingWizard;
